import tzLookup from 'tz-lookup';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

export const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

// Generate time options from 12:00 AM to 11:30 PM in 30-minute intervals
export const TIME_OPTIONS = (() => {
  const options = [];
  const periods = ['AM', 'PM'];

  for (let period of periods) {
    const hours12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    for (let hour of hours12) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time24 =
          period === 'AM' ? (hour === 12 ? 0 : hour) : hour === 12 ? 12 : hour + 12;

        const timeStr = `${time24.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        const displayTime = `${hour}:${minute.toString().padStart(2, '0')} ${period}`;

        options.push({
          value: timeStr,
          label: displayTime,
        });
      }
    }
  }

  return options;
})();

export const DAY_START_TIME = '00:00';
export const DAY_END_TIME = '23:30';
export const SLOT_GAP_MINUTES = 30;

export const timeToMinutes = timeStr => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
};

const sundayFirst = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/** `date`'s wall-clock reading in `timeZone` (falls back to the runtime's local zone). */
const zonedMoment = (date, timeZone) => (timeZone ? moment(date).tz(timeZone) : moment(date));

const dayKeyForDate = (date, timeZone) => sundayFirst[zonedMoment(date, timeZone).day()];

const minutesOfDay = (date, timeZone) => {
  const zoned = zonedMoment(date, timeZone);
  return zoned.hours() * 60 + zoned.minutes();
};

/**
 * IANA timezone (e.g. "Asia/Kolkata") for a store, derived from its address coordinates.
 * Returns null when coordinates are missing, so callers fall back to the runtime's local zone.
 */
export const getStoreTimeZone = origin => {
  const lat = origin?.lat;
  const lng = origin?.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  try {
    return tzLookup(lat, lng);
  } catch (e) {
    return null;
  }
};

const enabledSlots = dayData =>
  dayData?.enabled && Array.isArray(dayData.timeSlots) ? dayData.timeSlots : [];

/**
 * True only if at least one day is enabled with a valid time slot. Stricter than checking
 * `Object.keys(storeHours).length > 0`, which is also true for a `storeHours` map where every
 * day is present but disabled — callers that need to hide a "no hours configured" UI entirely
 * (rather than show an all-"Closed" week) should use this instead.
 */
export const hasEnabledStoreHours = storeHours =>
  DAYS_OF_WEEK.some(day => enabledSlots(storeHours?.[day.key]).some(slot => slot?.openTime && slot?.closeTime));

/**
 * Derives the current open/closed status of a store from its `storeHours` map (raw `HH:mm`
 * strings, no timezone of their own — they're wall-clock times at the store's location).
 *
 * `timeZone` should be the store's IANA zone (see `getStoreTimeZone`), so "now"/"today" are
 * evaluated at the store's location regardless of which clock (server during SSR, or a
 * visitor's browser in another timezone) is running this code. Omitting it falls back to the
 * runtime's local time, which is only correct when that runtime happens to share the store's
 * timezone.
 *
 * Times are returned as raw `HH:mm` strings/slots — formatting for display (e.g. via
 * `formatTime24WithIntl`) is left to the caller, since this helper has no `intl` dependency
 * and stays plain-JS testable.
 *
 * Returns:
 * - isOpen: whether `referenceDate` falls within one of today's enabled time slots
 * - todayOpenSlots: today's enabled `{ openTime, closeTime }` slots (empty array if none)
 * - nextOpenDayKey / nextOpenIsToday / nextOpenTime: the next upcoming opening, searching the
 *   rest of today first, then walking forward through the week (wrapping after Sunday). All
 *   three stay null when no day has any enabled slot (e.g. every day is disabled).
 */
export const getStoreHoursStatus = (storeHours, referenceDate = new Date(), timeZone = null) => {
  const todayKey = dayKeyForDate(referenceDate, timeZone);
  const nowMinutes = minutesOfDay(referenceDate, timeZone);

  const todaySlots = enabledSlots(storeHours?.[todayKey]);
  const isOpen = todaySlots.some(slot => {
    const open = timeToMinutes(slot?.openTime);
    const close = timeToMinutes(slot?.closeTime);
    return open != null && close != null && open <= nowMinutes && nowMinutes < close;
  });

  const todayOpenSlots = todaySlots.filter(slot => slot?.openTime && slot?.closeTime);

  let nextOpenDayKey = null;
  let nextOpenIsToday = false;
  let nextOpenTime = null;

  const laterTodayOpenTimes = todaySlots
    .map(slot => timeToMinutes(slot?.openTime))
    .filter(open => open != null && open > nowMinutes)
    .sort((a, b) => a - b);

  if (laterTodayOpenTimes.length > 0) {
    nextOpenDayKey = todayKey;
    nextOpenIsToday = true;
    const h = Math.floor(laterTodayOpenTimes[0] / 60)
      .toString()
      .padStart(2, '0');
    const m = (laterTodayOpenTimes[0] % 60).toString().padStart(2, '0');
    nextOpenTime = `${h}:${m}`;
  } else {
    const todayIndex = DAYS_OF_WEEK.findIndex(d => d.key === todayKey);
    for (let offset = 1; offset <= 6 && !nextOpenTime; offset += 1) {
      const day = DAYS_OF_WEEK[(todayIndex + offset) % DAYS_OF_WEEK.length];
      const slots = enabledSlots(storeHours?.[day.key])
        .map(slot => slot?.openTime)
        .filter(Boolean)
        .sort();
      if (slots.length > 0) {
        nextOpenDayKey = day.key;
        nextOpenTime = slots[0];
      }
    }
  }

  return {
    isOpen,
    todayKey,
    todayOpenSlots,
    nextOpenDayKey,
    nextOpenIsToday,
    nextOpenTime,
  };
};
